/**
 * Client-side resize + compress for product image uploads.
 *
 * Targets POS / catalog use (tiles + gallery), not print photography:
 * - max longest edge 1280px (sharp on ~640–800 CSS px at 2x)
 * - JPEG ~0.82 quality
 * - skip work when already small enough
 */

export type CompressImageOptions = {
	/** Longest side in pixels (default 1280) */
	maxEdge?: number;
	/** JPEG quality 0–1 (default 0.82) */
	quality?: number;
	/** Output MIME (default image/jpeg) */
	mimeType?: "image/jpeg" | "image/webp";
	/** If file is already under this size AND within maxEdge, skip re-encode (default 350_000) */
	skipIfUnderBytes?: number;
};

const DEFAULTS = {
	maxEdge: 1280,
	quality: 0.82,
	mimeType: "image/jpeg" as const,
	skipIfUnderBytes: 350_000,
};

function loadImage(file: File): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve(img);
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error("Failed to decode image"));
		};
		img.src = url;
	});
}

function canvasToBlob(
	canvas: HTMLCanvasElement,
	mimeType: string,
	quality: number,
): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (blob) resolve(blob);
				else reject(new Error("Canvas export failed"));
			},
			mimeType,
			quality,
		);
	});
}

/**
 * Resize/compress a user-selected image for product master upload.
 * Returns a new File (usually JPEG). On failure, returns the original file.
 */
export async function compressImageForUpload(
	file: File,
	options: CompressImageOptions = {},
): Promise<File> {
	const maxEdge = options.maxEdge ?? DEFAULTS.maxEdge;
	const quality = options.quality ?? DEFAULTS.quality;
	const mimeType = options.mimeType ?? DEFAULTS.mimeType;
	const skipIfUnderBytes = options.skipIfUnderBytes ?? DEFAULTS.skipIfUnderBytes;

	// Only process raster images
	if (!file.type.startsWith("image/") || file.type === "image/svg+xml") {
		return file;
	}

	try {
		const img = await loadImage(file);
		const srcW = img.naturalWidth || img.width;
		const srcH = img.naturalHeight || img.height;
		if (!srcW || !srcH) return file;

		const longest = Math.max(srcW, srcH);
		const needsResize = longest > maxEdge;
		const needsReencode =
			file.type !== mimeType || file.size > skipIfUnderBytes || needsResize;

		if (!needsResize && !needsReencode && file.size <= skipIfUnderBytes) {
			return file;
		}

		const scale = needsResize ? maxEdge / longest : 1;
		const dstW = Math.max(1, Math.round(srcW * scale));
		const dstH = Math.max(1, Math.round(srcH * scale));

		const canvas = document.createElement("canvas");
		canvas.width = dstW;
		canvas.height = dstH;
		const ctx = canvas.getContext("2d");
		if (!ctx) return file;

		// White background so transparent PNGs don't go black as JPEG
		ctx.fillStyle = "#ffffff";
		ctx.fillRect(0, 0, dstW, dstH);
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = "high";
		ctx.drawImage(img, 0, 0, dstW, dstH);

		let q = quality;
		let blob = await canvasToBlob(canvas, mimeType, q);

		// Second pass if still heavy (e.g. very detailed photo)
		if (blob.size > 600_000 && q > 0.65) {
			q = 0.7;
			blob = await canvasToBlob(canvas, mimeType, q);
		}
		if (blob.size > 600_000 && Math.max(dstW, dstH) > 1024) {
			const s2 = 1024 / Math.max(dstW, dstH);
			const w2 = Math.max(1, Math.round(dstW * s2));
			const h2 = Math.max(1, Math.round(dstH * s2));
			canvas.width = w2;
			canvas.height = h2;
			ctx.fillStyle = "#ffffff";
			ctx.fillRect(0, 0, w2, h2);
			ctx.drawImage(img, 0, 0, w2, h2);
			blob = await canvasToBlob(canvas, mimeType, 0.75);
		}

		const base =
			file.name.replace(/\.[^.]+$/, "") || "product";
		const ext = mimeType === "image/webp" ? "webp" : "jpg";
		return new File([blob], `${base}.${ext}`, {
			type: mimeType,
			lastModified: Date.now(),
		});
	} catch {
		return file;
	}
}
